'use client';
import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse } from 'axios';
import { getCookie } from 'cookies-next';
import { decrypt } from '../helpers/cryptoMethods';
import { getLanguage, isAppBridge } from '../helpers/appStorageMethod';
import utilsService from '../helpers/utilsService';

interface Tokens {
    access: string;
    refresh: string;
}
// Create axios instance.
const axiosInstance = axios.create({
    baseURL: process.env.BASE_URL,
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json',
    },
});

let accessToken: string = '';
let refreshToken: string = '';
let isRefreshing: boolean = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

export const setRefreshToken = (tk: string): void => {
    refreshToken = tk;
};

export const setAccessToken = (tk: string): void => {
    accessToken = tk;
};

const subscribeTokenRefresh = (cb: (token: string | null) => void): void => {
    refreshSubscribers.push(cb);
};

export const setToken = ({ access, refresh }: Tokens): void => {
    if (access) accessToken = access;
    if (refresh) refreshToken = refresh;
};

const onRefreshed = (token: string | null): void => {
    refreshSubscribers.forEach((cb) => cb(token));
    refreshSubscribers = [];
};

// 토큰 갱신이 최종 실패하면(리프레시 토큰 없음·리프레시 401·예외) 세션을 정리하고 로그인 화면으로 보낸다.
// 예전엔 여기서 reject 만 하고 쿠키를 안 지워, 로그아웃돼야 할 사용자가 캐시된 프로필 껍데기에 갇혔다
// (좀비 로그인, JWT_SECRET 교체·토큰 만료 시 전원이 이 상태가 됨, 2026-09-16). 대기 큐도 비워 요청이 안 멈추게 한다.
let hasForcedLogout = false;
const forceLogout = (): void => {
    // 큐에 쌓인 동시 요청을 풀어 준다(null → 각 구독자가 reject 처리). 안 그러면 스피너가 영영 돈다.
    onRefreshed(null);
    if (hasForcedLogout) return;
    hasForcedLogout = true;

    // 로그인 상태였던 사람만 로그인 화면으로 보낸다(게스트가 인증 API 401 로 튕기지 않게).
    const wasLoggedIn = !!(getCookie('_access') || getCookie('_refresh') || getCookie('user'));
    const _isAppBridge = isAppBridge();

    // 로그인·가입 화면에서는 세션만 정리하고 화면은 건드리지 않는다. 회원가입은 이 경로(/login 안의
    // 프로필 설정)에서 진행되는데, 아직 계정이 없어 인증 API 가 401 을 주는 게 정상이다.
    const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
    const onAuthScreen = /^\/login/.test(path) || /^\/auth/.test(path);

    try {
        // 소셜 인증 토큰(_fb_token)은 남긴다 — 가입 제출에 필요하다. 지우면 가입이 영영 완료되지 않는다.
        const { clearSessionCookies, deleteExpiredToken } = require('../helpers/cookiesMethod');
        clearSessionCookies();
        deleteExpiredToken();
    } catch (e) {
        /* noop */
    }

    if (_isAppBridge) {
        try {
            const { clearAppSessionStorage } = require('../helpers/appStorageMethod');
            clearAppSessionStorage();
        } catch (e) {
            /* noop */
        }
        if (!onAuthScreen) {
            try {
                (window as any).appBridge?.logout?.();
            } catch (e) {
                /* noop */
            }
        }
        return; // 앱은 네이티브가 로그인 화면을 띄운다. 웹뷰를 직접 이동시키지 않는다.
    }

    if (wasLoggedIn && !onAuthScreen && typeof window !== 'undefined') {
        // U6-6 ③: 세션이 끊겨 튕겨도 있던 화면으로 돌아온다.
        const search = window.location.search || '';
        window.location.replace(utilsService.buildLoginUrl(path + search));
    }
};

interface AdaptAxiosRequestConfig extends AxiosRequestConfig {
    headers: AxiosRequestHeaders;
}

// Request interceptor for API calls
// Request interceptor for API calls
axiosInstance.interceptors.request.use(
    async (config): Promise<AdaptAxiosRequestConfig> => {
        let rawToken = getCookie('_access');
        const _isAppBridge = isAppBridge();

        // Fallback to App Storage if cookie is missing on Bridge
        if (!rawToken && _isAppBridge) {
            try {
                const { getAppAccessToken } = require('../helpers/appStorageMethod');
                const appToken = await getAppAccessToken();
                if (appToken) {
                    rawToken = appToken;
                }
            } catch (e) {
                console.error('Failed to get access token from app storage', e);
            }
        }

        let finalToken = accessToken;

        if (rawToken) {
            try {
                const decryptedValue = decrypt(rawToken as string, process.env.SECRET_KEY);
                if (decryptedValue) {
                    try {
                        // Thử parse nếu là chuỗi JSON (có ngoặc kép)
                        const parsed = JSON.parse(decryptedValue);
                        finalToken = typeof parsed === 'string' ? parsed : decryptedValue;
                    } catch (e) {
                        // Nếu không parse được (chuỗi thuần), lấy trực tiếp
                        finalToken = decryptedValue;
                    }
                }
            } catch (e) {
                console.error('Failed to decrypt access token', e);
            }
        }

        // Làm sạch Token: Xóa bỏ dấu ngoặc kép hoặc khoảng trắng dư thừa
        if (finalToken && typeof finalToken === 'string') {
            finalToken = finalToken.replace(/^"|"$/g, '').trim();
        }

        // CHỐT CHẶN: Chỉ gửi Header nếu Token có định dạng hợp lệ
        if (finalToken && finalToken.length > 20 && finalToken.includes('.')) {
            config.headers.Authorization = `Bearer ${finalToken}`;
        }

        // Add language header
        try {
            const language = await getLanguage();
            config.headers['X-Language'] = language;
        } catch (error) {
            config.headers['X-Language'] = 'ko';
        }

        return config;
    },
    (error): any => {
        return Promise.reject(error);
    }
);

// axiosHandler.ts(전역 팝업 인터셉터, 이 인스턴스에 두 번째 response 인터셉터로 얹힌다)가
// error.status 로 410/403/401 팝업을 분기한다. 여기서 error.response.data(서버 JSON 바디)로
// 바꿔 reject 하면 원본 AxiosError 의 status/config 가 사라져 그 분기가 항상 빠진다
// (2026-09-29 발견 — 정지·탈퇴 계정이 아무 안내 없이 조용히 막히는 실제 결함이었다).
// 겉모양(호출부 162곳이 기대하는 평탄한 서버 응답 필드)은 그대로 두고 status 만 실어 보낸다.
const withStatus = (error: any): any => {
    const data = error?.response?.data;
    if (data && typeof data === 'object') {
        return Object.assign(data, { status: error.response.status, config: error.config });
    }
    return error;
};
const rejectWithStatus = (error: any): Promise<never> => Promise.reject(withStatus(error));

// Response interceptor for API calls
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async function (error: any) {
        const originalRequest = error.config;

        // Handle 401 Unauthorized for token refresh
        if (error.response && error.response.status === 401 && originalRequest?.url?.indexOf('/login') === -1 && !originalRequest._retry) {
            // Avoid recursion if the refresh token request itself fails
            if (originalRequest.url?.includes('/auth/refresh-token')) {
                forceLogout();
                return rejectWithStatus(error);
            }

            originalRequest._retry = true;

            if (!isRefreshing) {
                isRefreshing = true;
                const _isAppBridge = isAppBridge();
                try {
                    const refreshTokenCookie = getCookie('_refresh');
                    const { decrypt: localDecrypt } = require('../helpers/cryptoMethods');
                    const decryptedRawRefresh = refreshTokenCookie ? localDecrypt(refreshTokenCookie || '', process.env.SECRET_KEY) : '';
                    let decryptedRefreshToken = '';
                    if (decryptedRawRefresh) {
                        try {
                            const parsed = JSON.parse(decryptedRawRefresh);
                            decryptedRefreshToken = typeof parsed === 'string' ? parsed : decryptedRawRefresh;
                        } catch (e) {
                            decryptedRefreshToken = decryptedRawRefresh;
                        }
                    }
                    if (decryptedRefreshToken) decryptedRefreshToken = decryptedRefreshToken.replace(/^"|"$/g, '').trim();

                    // Nếu không có trong Cookie mà đang ở trên App, chủ động lấy từ App Storage
                    if (!decryptedRefreshToken && _isAppBridge) {
                        try {
                            const { getAppRefreshToken } = require('../helpers/appStorageMethod');
                            decryptedRefreshToken = await getAppRefreshToken();
                        } catch (e) {
                            console.error('Failed to get refresh token from app storage');
                        }
                    }

                    if (!decryptedRefreshToken) {
                        isRefreshing = false;
                        forceLogout();
                        return rejectWithStatus(error);
                    }

                    // Get the best available current token for the Authorization header
                    const rawAccessCookie = getCookie('_access');
                    let currentToken = accessToken;
                    if (rawAccessCookie) {
                        try {
                            const decryptedVal = localDecrypt(rawAccessCookie as string, process.env.SECRET_KEY);
                            if (decryptedVal) {
                                try {
                                    const parsed = JSON.parse(decryptedVal);
                                    currentToken = typeof parsed === 'string' ? parsed : decryptedVal;
                                } catch (e) {
                                    currentToken = decryptedVal;
                                }
                            }
                        } catch (e) {}
                    }
                    if (currentToken) currentToken = currentToken.replace(/^"|"$/g, '').trim();

                    // Fallback to App Storage if currentToken is empty and we are in App

                    // Call refresh token API directly using axios (not axiosInstance to avoid recursion)
                    // We send the header ONLY if currentToken is valid to avoid 500 Malformed Token crashes.
                    // Backend auth()->refresh() needs this header to know which token to refresh.
                    const res = await axios({
                        baseURL: process.env.BASE_URL,
                        url: '/auth/refresh-token',
                        method: 'POST',
                        data: { refresh_token: decryptedRefreshToken },
                        headers: (currentToken && currentToken.length > 10) ? {
                            Authorization: `Bearer ${currentToken}`
                        } : {}
                    });

                    const responseData = res.data?.data || res.data;
                    const newAccessToken = responseData?.access_token;
                    const newRefreshToken = responseData?.refresh_token || newAccessToken;
                    const userDataFromServer = responseData?.user;

                    // Update internal state
                    accessToken = newAccessToken;

                    console.log('[Token Refresh] Success', {
                        isAppBridge: _isAppBridge,
                        hasNewToken: !!newAccessToken,
                        hasUser: !!userDataFromServer,
                        url: originalRequest.url
                    });

                    // Update cookies and sync to App Bridge
                    const { setCookieUser } = require('../helpers/cookiesMethod');

                    // Always update local cookies with new tokens
                    setCookieUser({
                        user: userDataFromServer || {}, // Use server data or empty object
                        accessToken: newAccessToken,
                        refreshToken: newRefreshToken,
                        fireBaseToken: newAccessToken
                    });

                    // Sync tokens to App Storage for persistence
                    if (_isAppBridge) {
                        try {
                            const { setAppStorageUser } = require('../helpers/appStorageMethod');
                            await setAppStorageUser({
                                user: userDataFromServer,
                                accessToken: newAccessToken,
                                refreshToken: newRefreshToken,
                                fireBaseToken: newAccessToken
                            });
                            console.log('[Token Refresh] Synced to app storage successfully');
                        } catch (e) {
                            console.error('[Token Refresh] Failed to sync to app storage:', e);
                        }
                    }

                    isRefreshing = false;
                    onRefreshed(newAccessToken);

                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    return axiosInstance(originalRequest);
                } catch (e) {
                    isRefreshing = false;
                    forceLogout();
                    return Promise.reject(e);
                }
            }

            // Queue concurrent requests while refreshing
            const retryOrigReq = new Promise((resolve, reject) => {
                subscribeTokenRefresh((token) => {
                    // token === null 이면 갱신이 실패한 것 — 대기 요청을 매달아 두지 않고 거절한다.
                    if (!token) {
                        reject(withStatus(error));
                        return;
                    }
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    resolve(axiosInstance(originalRequest));
                });
            });
            return retryOrigReq;
        }

        // Handle other specific errors
        if (error.response && (error.response.status === 410 || error.response.status === 403)) {
            return rejectWithStatus(error);
        }

        // Handle network errors
        if (error?.code !== 'ERR_CANCELED' && error?.code === 'ERR_NETWORK') {
            return Promise.reject({
                ...error,
                config: error?.config,
                response: error?.response,
                status: 500,
                msg: error?.message ?? 'Network error occurred. Please try again later.'
            });
        }

        return rejectWithStatus(error);
    }
);

export default axiosInstance;
